/**
 * MultiValueSelect  (SM-22 / DA-2295)
 *
 * The D (discrete) HP read operator's value position isn't always one value — the
 * grammar allows a `-`-joined set meaning "in {…}" (§3.4 of the design doc). This is
 * the multi-select counterpart to SearchableSelect for exactly that position; the
 * joined-with-'-' string it produces is the literal mapper value, unchanged.
 */
import React from 'react'
import { Autocomplete, TextField } from '@mui/material'

export interface MultiValueSelectOption {
  value: string
  label: string
}

export interface MultiValueSelectProps {
  label: string
  values: string[]
  onChange: (values: string[]) => void
  options: MultiValueSelectOption[]
  disabled?: boolean
  placeholder?: string
  /** Async catalog still loading — announces via the listbox loading text. */
  loading?: boolean
  /** Error styling on the field; pair with `helperText` for the message. */
  error?: boolean
  /** Helper/error text rendered inside the FormControl (aria-describedby-linked). */
  helperText?: React.ReactNode
  /** Overrides MUI's generic "No options" so empty vs error vs loading read differently. */
  noOptionsText?: React.ReactNode
}

export const MultiValueSelect: React.FC<MultiValueSelectProps> = ({
  label,
  values,
  onChange,
  options,
  disabled = false,
  placeholder,
  loading = false,
  error = false,
  helperText,
  noOptionsText,
}) => {
  const selected = options.filter((o) => values.includes(o.value))

  return (
    <Autocomplete
      multiple
      size="small"
      options={options}
      value={selected}
      onChange={(_e, opts) => onChange(opts.map((o) => o.value))}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      disabled={disabled}
      loading={loading}
      loadingText="Loading…"
      noOptionsText={noOptionsText ?? 'No options'}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selected.length ? undefined : placeholder}
          error={error}
          helperText={helperText}
        />
      )}
    />
  )
}
